import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import env from "../../../../config/env";
import "./index.css";
import CommonModal from "../../../Settings/components/ResetContext/components/DumpModal";
import IntegrationModal from "./IntegrationModal";
import PromptIntroStep from "./components/PromptIntroStep";
import PromptDetailsForm from "./components/PromptDetailsForm";
import DeleteTaskModal from "./components/DeleteTaskModal";
import {
  createInitialOutputOption,
  createProviderSelectOption,
  DEFAULT_OUTPUT_OPTION,
  formatToTimeInput,
  isChannelProvider,
  mergeChannels,
  normalizeProviderId,
  parseChannelsPayload,
  parseProvidersPayload,
  convertLocalToUTC,
} from "./utils";
import { useUserInfo } from "../../../../context/user";
import { useAppServices } from "../../../../hook/services";
import toast from "react-hot-toast";
import moment from "moment-timezone";

const PromptModal = ({
  isOpen,
  isModalOpen,
  onClose,
  justCloseIt,
  getUpdatePromptsData,
  editadata,
  setIsEditPrompt,
  utcOffset,
  autoloadfirsttext,
  assistantData,
  asistantSelected,
  timeWithZone,
  time_zone,
  renderAs = "modal",
}) => {
  const BASE_URL = `${env.API_URL}/v1`;
  const isPageMode = renderAs === "page";
  const modalIsOpen = isPageMode
    ? true
    : typeof isOpen === "boolean"
      ? isOpen
      : Boolean(isModalOpen);
  const isEdit = Boolean(editadata);
  const isScheduledTask =
    editadata?.repeat && editadata.repeat.toLowerCase() !== "none";
  const selectedAssistant = asistantSelected || assistantData?.[0] || null;

  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const Service = useAppServices();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [askToDeleteModal, setAskToDeleteModal] = useState(false);

  const [promptName, setPromptName] = useState("");
  const [promptContent, setPromptContent] = useState("");
  const [promptSetAs, setPromptSetAs] = useState("personal");
  const [outputOption, setOutputOption] = useState(DEFAULT_OUTPUT_OPTION);
  const [slackChannel, setSlackChannel] = useState({});
  const [openIntegrationModal, setOpenIntegrationModal] = useState(false);
  const [requiredIntegrations, setRequiredIntegrations] = useState([]);
  const [integrationId, setIntegrationId] = useState(null);
  const [utcTime, setUtcTime] = useState(null);
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(moment().format("YYYY-MM-DD"));
  const [scheduleRunCount, setScheduleRunCount] = useState(1);
  const [scheduleFrequency, setScheduleFrequency] = useState("none");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isScheduleActive, setIsScheduleActive] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(1);
  const [selectedWebhookId, setSelectedWebhookId] = useState(null);

  const [channelList, setChannelList] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingMoreChannels, setLoadingMoreChannels] = useState(false);
  const [hasMoreChannels, setHasMoreChannels] = useState(false);
  const [nextChannelPageToken, setNextChannelPageToken] = useState(null);
  const [channelSearchQuery, setChannelSearchQuery] = useState("");
  const [activeChannelProvider, setActiveChannelProvider] = useState(null);
  const [activeChannelIntegration, setActiveChannelIntegration] =
    useState(null);
  const [rqIntegration, setRqIntegration] = useState(null);
  const channelSearchTimer = useRef(null);

  const [promptNameError, setPromptNameError] = useState(null);
  const [promptContentError, setPromptContentError] = useState(null);
  const [promptStep, setPromptStep] = useState(0);
  const [promptQuery, setPromptQuery] = useState("");
  const [promptQueryError, setPromptQueryError] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const closeModal = () => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }

    if (typeof justCloseIt === "function") {
      justCloseIt();
    }
  };

  const resetChannelState = useCallback(() => {
    setChannelList([]);
    setSlackChannel({});
    setHasMoreChannels(false);
    setNextChannelPageToken(null);
    setChannelSearchQuery("");
    setActiveChannelProvider(null);
    setActiveChannelIntegration(null);
  }, []);

  useEffect(() => {
    if (!modalIsOpen) return;

    setProcessing(false);
    setDeleteProcessing(false);
    setAskToDeleteModal(false);
    resetChannelState();

    setPromptName(editadata?.title || "");
    setPromptContent(
      editadata?.prompt_value ||
      '<p>Draft a client follow-up email. <strong><span style="background-color: rgb(254, 240, 138);">[Paste decisions here]</span></strong> Create a professional follow-up email from call notes, including actions and next steps. <strong><span style="background-color: rgb(254, 240, 138);">Follow-up email from call.</span></strong></p>',
    );
    setPromptSetAs(editadata?.set_as || "personal");
    setOutputOption(createInitialOutputOption(editadata?.output));
    setSlackChannel({ label: editadata?.channel_name || '', value: editadata?.channel });
    setOpenIntegrationModal(false);
    setRequiredIntegrations([]);
    setIntegrationId(editadata?.integration_id || null);
    setUtcTime(editadata?.utcTime || null);
    setSelectedWeekDays(
      editadata?.selectedWeekDays?.length > 0 ? editadata?.selectedWeekDays : editadata?.weekdays || [],
    );
    setScheduleDate(editadata?.scheduleDate || "");
    setScheduleRunCount(editadata?.runCount || 1);
    setScheduleFrequency(editadata?.repeat || "none");
    setScheduleTime(
      editadata?.scheduleTime ? timeWithZone : "",
    );
    setIsScheduleActive(Boolean(editadata?.active));
    setSelectedWebhookId(null);
    setEstimatedTime(
      editadata?.estimated_time !== undefined && editadata?.estimated_time !== null
        ? String(editadata.estimated_time)
        : 1,
    );

    setPromptNameError(null);
    setPromptContentError(null);
    setPromptStep(isEdit ? 2 : 0);
    setPromptQuery(isEdit ? "" : autoloadfirsttext || "");
    setPromptQueryError(false);
    setRqIntegration(null);
    setSelectedWebhookId(editadata?.webhook_source || null);

    const seededTimestamp =
      editadata?.updatedAt ||
      editadata?.updated_at ||
      editadata?.createdAt ||
      editadata?.created_at ||
      null;
    setLastSavedAt(seededTimestamp ? new Date(seededTimestamp).getTime() : null);
  }, [autoloadfirsttext, editadata, isEdit, modalIsOpen, resetChannelState]);

  useEffect(() => {
    return () => {
      if (channelSearchTimer.current) {
        clearTimeout(channelSearchTimer.current);
      }
    };
  }, []);

  const handleScheduleFrequencyChange = (frequency) => {
    setScheduleFrequency(frequency);
    setIsScheduleActive(frequency === "none" ? false : true);
  };

  const handleSchedulePause = () => {
    setIsScheduleActive((currentValue) => !currentValue);
  };

  const handleScheduleTimeChange = (time) => {
    setScheduleTime(time);
    setUtcTime(convertLocalToUTC(time, utcOffset));
  };

  const loadChannels = useCallback(
    async (providerId, integration, fetchOptions = {}) => {
      const {
        search = "",
        pageToken = "",
        append = false,
        preferredChannelId = null,
      } = fetchOptions;

      if (!isChannelProvider(providerId)) return;

      try {
        if (append) {
          setLoadingMoreChannels(true);
        } else {
          setLoadingChannels(true);
        }

        setActiveChannelProvider(providerId || null);
        setActiveChannelIntegration(integration || null);

        if (!append) {
          setChannelSearchQuery(search);
        }

        const response = await axios.get(
          `${BASE_URL}/prompts/providers/channels`,
          {
            params: {
              profile_id: userActiveLocation?.id,
              user_id: userInfo?.userId,
              channel_type: "bot",
              include_private: true,
              ...(integration ? { integration_id: integration } : {}),
              integration: providerId,
              ...(search ? { search } : {}),
              ...(pageToken ? { page_token: pageToken } : {}),
            },
            timeout: 600000,
          },
        );

        const providerPayload = response?.data?.data || {};
        const { channels, pagination } = parseChannelsPayload(providerPayload);
        const normalizedChannels = Array.isArray(channels) ? channels : [];

        setChannelList((previousChannels) =>
          append
            ? mergeChannels(previousChannels, normalizedChannels)
            : normalizedChannels,
        );

        const nextPageToken = pagination?.next_page_token || null;
        setNextChannelPageToken(nextPageToken);
        setHasMoreChannels(Boolean(pagination?.has_next && nextPageToken));

        if (!append) {
          setSlackChannel((currentChannel) => {
            if (currentChannel?.value) {
              const findChannel = normalizedChannels.find(
                (channel) => channel.channel_id === currentChannel.value,
              );
              if (findChannel && !currentChannel.label) {
                return { value: findChannel.channel_id, label: findChannel.channel_name };
              }
              return currentChannel;
            }
          });
        }
      } catch (error) {
        console.error("Error fetching provider channels:", error);
        toast.error("Unable to load channels right now.");
      } finally {
        setLoadingChannels(false);
        setLoadingMoreChannels(false);
      }
    },
    [BASE_URL, userInfo?.userId, userActiveLocation?.id],
  );

  const fetchProviders = useCallback(
    async (targetProvider = null) => {
      if (!userActiveLocation?.id || !userInfo?.userId) return;

      try {
        const response = await axios.get(`${BASE_URL}/prompts/providers`, {
          params: {
            profile_id: userActiveLocation?.id,
            user_id: userInfo?.userId,
            agency_id: userInfo?.agency_id,
          },
          timeout: 10000,
        });

        const providers = parseProvidersPayload(
          response?.data?.data?.data || {},
        );
        setRequiredIntegrations(providers);

        const nextProviderId = normalizeProviderId(
          targetProvider || editadata?.output,
        );

        if (!isChannelProvider(nextProviderId)) return;

        const provider = providers.find(
          (item) => normalizeProviderId(item) === nextProviderId,
        );

        if (!provider) return;

        const nextOption = createProviderSelectOption(provider);
        const nextIntegrationId = provider.integration_id || null;

        setOutputOption(nextOption);
        setIntegrationId(nextIntegrationId);

        if (!provider.connected) {
          setRqIntegration(nextProviderId);
          setOpenIntegrationModal(true);
          return;
        }

        setOpenIntegrationModal(false);
        await loadChannels(nextProviderId, nextIntegrationId, {
          search: "",
          pageToken: "",
          append: false,
          preferredChannelId: editadata?.channel || null,
        });
      } catch (error) {
        console.error("Error fetching providers:", error);
        toast.error("Unable to load output providers right now.");
      }
    },
    [
      BASE_URL,
      userInfo?.userId,
      userInfo?.agency_id,
      userActiveLocation?.id,
      editadata?.channel,
      editadata?.output,
      loadChannels,
    ],
  );

  useEffect(() => {
    if (!modalIsOpen) return;
    fetchProviders();
  }, [fetchProviders, modalIsOpen]);

  const handleOutputOptionChange = async (option) => {
    const nextOption = option || DEFAULT_OUTPUT_OPTION;
    const providerId = normalizeProviderId(nextOption?.value);

    setOutputOption(nextOption);

    if (channelSearchTimer.current) {
      clearTimeout(channelSearchTimer.current);
    }

    resetChannelState();

    if (!isChannelProvider(providerId)) {
      setIntegrationId(null);
      setOpenIntegrationModal(false);
      setRqIntegration(null);
      return;
    }

    const provider = requiredIntegrations.find(
      (item) => normalizeProviderId(item) === providerId,
    );
    const nextIntegrationId = provider?.integration_id || null;

    setIntegrationId(nextIntegrationId);
    setActiveChannelProvider(providerId);
    setActiveChannelIntegration(nextIntegrationId);

    if (provider && !provider.connected) {
      setRqIntegration(providerId);
      setOpenIntegrationModal(true);
      return;
    }

    setOpenIntegrationModal(false);
    await loadChannels(providerId, nextIntegrationId, {
      search: "",
      pageToken: "",
      append: false,
    });
  };

  const handleChannelSearchInputChange = (inputValue, actionMeta) => {
    if (actionMeta?.action !== "input-change") return inputValue;

    const searchValue = inputValue || "";
    setChannelSearchQuery(searchValue);

    if (channelSearchTimer.current) {
      clearTimeout(channelSearchTimer.current);
    }

    if (!activeChannelProvider) return inputValue;

    channelSearchTimer.current = setTimeout(() => {
      loadChannels(activeChannelProvider, activeChannelIntegration, {
        search: searchValue,
        pageToken: "",
        append: false,
      });
    }, 350);

    return inputValue;
  };

  const handleChannelMenuScrollToBottom = () => {
    if (!activeChannelProvider) return;
    if (!hasMoreChannels || !nextChannelPageToken) return;
    if (loadingChannels || loadingMoreChannels) return;

    loadChannels(activeChannelProvider, activeChannelIntegration, {
      search: channelSearchQuery,
      pageToken: nextChannelPageToken,
      append: true,
    });
  };

  const handlePromptSubmit = async () => {
    if (processing) return;

    setPromptNameError(null);
    setPromptContentError(null);

    let hasError = false;

    if (!promptName.trim()) {
      setPromptNameError("Prompt Name is required");
      hasError = true;
    }

    if (!promptContent.trim()) {
      setPromptContentError("Prompt Content is required");
      hasError = true;
    }

    if (hasError) return;

    setProcessing(true);

    try {
      const authorEntry = {
        id: userInfo?.userId,
        type: "author",
        username: userInfo?.userName,
      };

      const existingUserIds = Array.isArray(editadata?.userId)
        ? editadata.userId
        : [];
      const sharedUserEntries = existingUserIds.filter(
        (entry) => entry?.type === "shared",
      );
      const userIdPayload = isEdit
        ? [authorEntry, ...sharedUserEntries]
        : [authorEntry];

      const payload = {
        title: promptName.trim(),
        category: editadata?.category || "",
        sub_category: editadata?.sub_category || "",
        prompt_value: promptContent,
        type: "user",
        userId: userIdPayload,
        locationId: userActiveLocation?.id,
        estimated_time: estimatedTime,
        set_as: promptSetAs,
        output: outputOption?.value || DEFAULT_OUTPUT_OPTION.value,
        channel: isChannelProvider(outputOption?.value) ? slackChannel.value : "",
        channel_name: isChannelProvider(outputOption?.value) ? slackChannel.label : "",
        scheduleTime: `${scheduleDate} ${scheduleTime || ""}`,
        repeat: scheduleFrequency,
        active: scheduleFrequency === "none" ? false : isScheduleActive,
        utcTime: scheduleFrequency === "none" ? null : utcTime,
        time_zone: time_zone,
        webhook_source: scheduleFrequency === "webhook" ? selectedWebhookId : null,
      };

      if (integrationId) {
        payload.integration_id = integrationId;
      }

      if (
        scheduleFrequency === "weekly" ||
        scheduleFrequency === "weekdays" ||
        scheduleFrequency === "weekends"
      ) {
        payload.weekDays = selectedWeekDays;
        payload.runCount = scheduleRunCount;
      }

      if (scheduleFrequency === "monthly" || scheduleFrequency === "yearly") {
        payload.scheduleDate = scheduleDate;
        payload.runCount = scheduleRunCount;
      }

      console.log("[PromptModal] save payload:", payload);

      let savedTaskId = editadata?._id;

      if (isEdit) {
        payload._id = editadata?._id;
        await axios.put(`${BASE_URL}/prompts/update/scheduled`, payload);
      } else {
        payload.assistant_name = selectedAssistant?.assistant_name;
        payload.assistant_id = selectedAssistant?.assistant_id;
        const createRes = await axios.post(`${BASE_URL}/prompts`, payload);
        savedTaskId = createRes?.data?.data?._id || savedTaskId;
      }

      // Link the selected webhook to the task (uses the existing convoy endpoint
      // POST /convoy/sources/:source_id/tasks/:task_id).
      if (scheduleFrequency === "webhook" && selectedWebhookId && savedTaskId) {
        const { error: linkError } = await Service.convoy.add_webhook_to_task({
          source_id: selectedWebhookId,
          task_id: savedTaskId,
          payload: {
            user_id: userInfo?._id,
            endpoint_url: `${BASE_URL}/convoy/receive/${selectedWebhookId}/${savedTaskId}`,
          },
        });
        if (linkError) {
          console.error("Error linking webhook to task:", linkError);
          toast.error(
            linkError?.message || "Task saved, but linking the webhook failed.",
          );
        }
      }

      setLastSavedAt(Date.now());
      getUpdatePromptsData?.();

      if (typeof setIsEditPrompt === "function") {
        setIsEditPrompt(false);
      }

      if (isPageMode && isEdit) {
        toast.success("Task saved");
      } else {
        closeModal();
      }
    } catch (error) {
      console.error("Error saving task prompt:", error);
      toast.error("Something went wrong while saving the task.");
    } finally {
      setProcessing(false);
    }
  };

  const handleGeneratePrompt = async () => {
    if (!promptQuery.trim()) {
      setPromptQueryError(true);
      return;
    }

    setPromptQueryError(false);
    setPromptStep(1);

    try {
      const customTaskAssistant = await axios.get(
        `${BASE_URL}/assistant/custom-task-agent`,
        {
          params: {
            agency_id: userActiveLocation?.agency_id,
            assistant_type: "Custom Task",
          },
        },
      );

      const assistantId =
        customTaskAssistant?.data?.data?.assistant_id ||
        selectedAssistant?.assistant_id;

      const response = await axios.post(`${BASE_URL}/prompts/generate`, {
        locationId: userActiveLocation?.id,
        username: userInfo?.userName,
        promptQuery: promptQuery.trim(),
        assistant_id: assistantId,
        user_id: userInfo?.userId,
      });

      setPromptStep(2);
      setPromptName(response?.data?.data?.title || "");
      setPromptContent(response?.data?.data?.content || "");
    } catch (error) {
      console.error("Error generating task prompt:", error);
      toast.error("Unable to generate the task right now.");
      setPromptStep(5);
    }
  };

  const handleDeleteTask = async () => {
    if (!editadata?._id || deleteProcessing) return;

    setDeleteProcessing(true);

    try {
      const endpoint = isScheduledTask
        ? "/prompts/delete/scheduled"
        : "/chat/promptDelete";

      await axios.delete(`${BASE_URL}${endpoint}?_id=${editadata._id}`);

      navigate("/");
      setAskToDeleteModal(false);
      closeModal();
      getUpdatePromptsData?.();

      if (typeof setIsEditPrompt === "function") {
        setIsEditPrompt(false);
      }
    } catch (error) {
      console.error("Error deleting task prompt:", error);
      toast.error("Something went wrong while deleting the task.");
    } finally {
      setDeleteProcessing(false);
    }
  };

  const handleGeneratePromptOnEnter = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleGeneratePrompt();
    }
  };

  const formContent =
    !isEdit && promptStep !== 2 ? (
      <PromptIntroStep
        step={promptStep}
        promptQuery={promptQuery}
        promptQueryError={promptQueryError}
        onPromptQueryChange={setPromptQuery}
        onSubmit={handleGeneratePrompt}
        onKeyDown={handleGeneratePromptOnEnter}
        onClose={closeModal}
      />
    ) : (
      <PromptDetailsForm
        isEdit={isEdit}
        promptName={promptName}
        promptNameError={promptNameError}
        onPromptNameChange={(value) => {
          setPromptName(value);
          setPromptNameError(null);
        }}
        isScheduleActive={isScheduleActive}
        onSchedulePause={handleSchedulePause}
        outputOption={outputOption}
        requiredIntegrations={requiredIntegrations}
        channelList={channelList}
        slackChannel={slackChannel}
        onOutputOptionChange={handleOutputOptionChange}
        onSlackChannelChange={(selectedOption) =>
          setSlackChannel({ value: selectedOption?.value || "", label: selectedOption?.label || "" })
        }
        onChannelSearchInputChange={handleChannelSearchInputChange}
        onChannelMenuScrollToBottom={handleChannelMenuScrollToBottom}
        loadingChannels={loadingChannels}
        loadingMoreChannels={loadingMoreChannels}
        hasMoreChannels={hasMoreChannels}
        channelSearchQuery={channelSearchQuery}
        scheduleFrequency={scheduleFrequency}
        scheduleTime={scheduleTime}
        scheduleDate={scheduleDate}
        scheduleRunCount={scheduleRunCount}
        selectedWeekDays={selectedWeekDays}
        onScheduleFrequencyChange={handleScheduleFrequencyChange}
        onScheduleTimeChange={handleScheduleTimeChange}
        onScheduleDateChange={setScheduleDate}
        onScheduleRunCountChange={setScheduleRunCount}
        onSelectedWeekDaysChange={setSelectedWeekDays}
        promptContent={promptContent}
        promptContentError={promptContentError}
        onPromptContentChange={(value) => {
          setPromptContent(value);
          setPromptContentError(null);
        }}
        promptSetAs={promptSetAs}
        onPromptSetAsChange={setPromptSetAs}
        estimatedTime={estimatedTime}
        onEstimatedTimeChange={setEstimatedTime}
        processing={processing}
        onSubmit={handlePromptSubmit}
        onDeleteClick={() => setAskToDeleteModal(true)}
        onClose={closeModal}
        agencyId={userActiveLocation?.agency_id}
        lastSavedAt={lastSavedAt}
        renderAs={renderAs}
        editadata={editadata}
        getUpdatePromptsData={getUpdatePromptsData}
        selectedWebhookId={selectedWebhookId}
        onSelectedWebhookIdChange={setSelectedWebhookId}
      />
    );

  return (
    <div>
      {isPageMode ? (
        <div className="w-full h-full flex justify-center overflow-hidden">
          <div className="w-[90%] v3chat__input !h-full overflow-y-auto mt-5">
            <button
              type="button"
              onClick={closeModal}
              className="text-[13px] !text-[#757575] hover:text-black flex items-center gap-1 mb-4"
            >
              ← Return to Tasks
            </button>
            {formContent}
          </div>
        </div>
      ) : (
        <CommonModal
          isOpen={modalIsOpen}
          onClose={closeModal}
          showAlert={false}
          title={
            <p className="text-left font-bold text-[16px]">
              {isEdit ? "Edit Task" : "Add Task"}
            </p>
          }
        >
          {formContent}
        </CommonModal>
      )}

      <DeleteTaskModal
        isOpen={askToDeleteModal}
        isScheduledTask={isScheduledTask}
        loading={deleteProcessing}
        onClose={() => setAskToDeleteModal(false)}
        onConfirm={handleDeleteTask}
      />

      {openIntegrationModal && (
        <IntegrationModal
          isOpen={openIntegrationModal}
          onClose={() => {
            setOpenIntegrationModal(false);
            setRqIntegration(null);
          }}
          rqIntegration={rqIntegration}
          requiredIntegrations={requiredIntegrations}
          integration_id={integrationId}
          onAllConnected={() => {
            if (rqIntegration) {
              fetchProviders(rqIntegration);
            }
          }}
        />
      )}
    </div>
  );
};

export default PromptModal;
